import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Phone from "@insite/mobius/Icons/Phone";
import Img from "@insite/mobius/Img/Img";
import { GridContainer, GridItem, Link } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser"
import ReactDOMServer from 'react-dom/server';
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,   
});
type Props = OwnProps & HasProduct &
ReturnType<typeof mapStateToProps>;
// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    headingText = "headingText",
    subHeadingText = "subHeadingText",
    linkIcon = "linkIcon",
    linkIconAltText = "linkIconAltText",
}

interface OwnProps extends WidgetProps  {
    fields: {
        [fields.headingText]: string;
        [fields.subHeadingText]: string;
        [fields.linkIcon]: string;
        [fields.linkIconAltText]: string;
    };
}

export interface DsgNeedHelpStyles {
    needHelpcontainer?: GridContainerProps;
    needHelpGridItem?: GridItemProps;
    headingText?: InjectableCss;
    subHeadingText?: InjectableCss;
    linkText?: InjectableCss;
}


export const dsgNeedHelpStyles: DsgNeedHelpStyles = {
    needHelpGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            color: ${colorPallete.grey800};
            row-gap: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        row-gap: 16px;
                    `,
                ],
                "min",
            )}
        `
    },
    headingText: {
        css: css`
            font: ${bodyFonts.body1SemiBoldLg.fontWeight}  ${bodyFonts.body1SemiBoldLg.fontSize}/ ${bodyFonts.body1SemiBoldLg.lineHeight}  ${bodyFonts.body1SemiBoldLg.fontFamily};
            margin: 0;
            color: ${colorPallete.grey800};
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        font-size: ${headlineFonts.headline4SemiBoldLg.fontSize};
                        line-height: ${headlineFonts.headline4SemiBoldLg.lineHeight}; 
                    `,
                ],
                "min",
            )}
        `
    },
    subHeadingText: {
        css: css`
            color: ${colorPallete.grey800};
            &, p {
                margin-bottom: 0;
                color: ${colorPallete.grey800};
                font: ${labelFonts.label1Regular.fontWeight}  ${labelFonts.label1Regular.fontSize}/ ${labelFonts.label1Regular.lineHeight}  ${labelFonts.label1Regular.fontFamily};
            }
            strong {
                font-weight: 600;
            }
            a {
                text-decoration: underline;
                &:hover {
                    color: ${colorPallete.primaryMaroon500};
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        &, p {
                            font-size: ${bodyFonts.body2RegularLg.fontSize};
                            line-height: ${bodyFonts.body2RegularLg.lineHeight}; 
                        }
                    `,
                ],
                "min",
            )}
        `
    },
    linkText: {
        css: css`
            span {
                display: flex;
                column-gap: 8px;
                align-items: center;
                font: ${labelFonts.label1Regular.fontWeight}  ${labelFonts.label1Regular.fontSize}/ ${labelFonts.label1Regular.lineHeight}  ${labelFonts.label1Regular.fontFamily};
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${bodyFonts.body2RegularLg.fontSize};
                            line-height: ${bodyFonts.body2RegularLg.lineHeight}; 
                        `,
                    ],
                    "min",
                )}
            }
        `
    }
}

const styles = dsgNeedHelpStyles;

const svgToDataUri = (Component: React.ElementType) => {
    const svgString = ReactDOMServer.renderToStaticMarkup(<Component />);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
};
  

const DsgNeedHelp:React.FC<Props> = ({ session,fields }) => {
    const phoneSrc = svgToDataUri(Phone);

    return (
        <>
            <StyledWrapper className="boxGrey">
                <GridContainer {...styles.needHelpcontainer}>
                    <GridItem {...styles.needHelpGridItem}>
                        <Typography as="h2" variant="h5" {...styles.headingText}>
                            {parse(fields.headingText, parserOptions)}
                        </Typography>
                        <Typography as="p" {...styles.subHeadingText}>
                            {parse(fields.subHeadingText, parserOptions)}
                        </Typography>
                        <Link href={`tel:${session.pickUpWarehouse?.phone}`} {...styles.linkText}>
                            {fields.linkIcon && <Img src={fields.linkIcon} altText={fields.linkIconAltText || ''} />}
                            {!fields.linkIcon && <Phone/>}
                            {session.pickUpWarehouse?.phone}
                        </Link>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </>
    );
    
};



const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            fieldType: "General",
            name: fields.headingText,
            editorTemplate: "TextField",
            displayName: "Heading",
            defaultValue: "Need help?"
        },
        {
            fieldType: "Contextual",
            name: fields.subHeadingText,
            editorTemplate: "RichTextField",
            displayName: "Subheading Text",
            defaultValue: "<p>Visit our <a href='#'><strong>Help</strong></a> section or call/text your local branch at the number below.</p>",
            extendedConfig: { height: 170 },
            expandedToolbarButtons: {
                moreMisc: {
                    buttons: [
                        "insertLink",
                        "insertImage",
                        "insertHR",
                        "specialCharacters",
                        "insertVideo",
                        "insertFile",
                    ],
                    buttonsVisible: 3,
                },
                code: {
                    buttons: ["selectAll", "html", "help"],
                    buttonsVisible: 3,
                },
            },
        },
        {
            fieldType: "General",
            name: fields.linkIcon,
            displayName: 'Image',
            editorTemplate: "ImagePickerField",
            defaultValue: "/userfiles/phone.png",
        },
        {
            fieldType: "General",
            name: fields.linkIconAltText,
            editorTemplate: "TextField",
            displayName: "Image Alt Text",
            defaultValue: "Telephone:",
        },
    ],
};

const widgetModule: WidgetModule = {
    component:connect(mapStateToProps) (withProduct(DsgNeedHelp)),
    definition,
};

export default widgetModule;
